import React from 'react';
import { Grid, TextField, Alert, Box } from '@mui/material';
import PaymentIcon from '@mui/icons-material/Payment';
import { Controller } from 'react-hook-form';
import AccordionCard from '../../../Shared/Components/AccordionCard/AccordionCard';
import type { InvoicePrepaymentPaymentSectionProps } from './InvoicePrepaymentPaymentSection.types';
import { InvoiceStatus } from '../../Types/invoicesTypes';

/**
 * Section for Prepayment invoice payment details.
 * Prepayment invoices MUST have status=Paid, paidAmount>0, and paidAt filled.
 * This is enforced by backend FluentValidation.
 */
const InvoicePrepaymentPaymentSection: React.FC<InvoicePrepaymentPaymentSectionProps> = ({
  control,
  errors,
}) => {
  return (
    <AccordionCard title="Dane zapłaty (Faktura zaliczkowa)" icon={<PaymentIcon color="primary" />}>
      <Box mb={2}>
        <Alert severity="info">
          <strong>Reguła biznesowa:</strong> Faktura zaliczkowa jest wystawiana PO otrzymaniu wpłaty
          bankowej. Status musi być "Opłacona", kwota zapłacona musi być równa sumie brutto (z VAT)
          pozycji faktury, data zapłaty wymagana.
        </Alert>
      </Box>

      <Grid container spacing={2}>
        {/* S T A T U S */}
        <Grid size={{ xs: 12, md: 4 }} >
          <Controller
            name="status"
            control={control}
            rules={{ required: 'Status jest wymagany dla faktury zaliczkowej' }}
            render={({ field }) => (
              <TextField
                select
                label="Status faktury *"
                value={field.value ?? InvoiceStatus.Paid}
                onChange={field.onChange}
                onBlur={field.onBlur}
                fullWidth
                error={!!errors.status}
                helperText={errors.status?.message || 'Status musi być "Opłacona" (Paid)'}
                SelectProps={{
                  native: true,
                }}
              >
                <option value={InvoiceStatus.Paid}>Opłacona (Paid)</option>
                <option value={InvoiceStatus.Issued} disabled>
                  Wystawiona (Issued) - niedozwolone dla zaliczki
                </option>
                <option value={InvoiceStatus.Draft} disabled>
                  Szkic (Draft) - niedozwolone dla zaliczki
                </option>
              </TextField>
            )}
          />
        </Grid>

        {/* K W O T A   Z A P Ł A C O N A */}
        <Grid size={{ xs: 12, md: 4 }} >
          <Controller
            name="paidAmount"
            control={control}
            rules={{
              required: 'Kwota zapłacona jest wymagana',
              min: { value: 0.01, message: 'Kwota musi być większa od 0' },
            }}
            render={({ field }) => (
              <TextField
                type="number"
                label="Kwota zapłacona *"
                value={field.value ?? ''}
                onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                onBlur={field.onBlur}
                fullWidth
                error={!!errors.paidAmount}
                helperText={
                  errors.paidAmount?.message ||
                  'Kwota brutto (z VAT), która została wpłacona przez klienta. Musi być równa sumie brutto pozycji faktury.'
                }
                inputProps={{
                  step: '0.01',
                  min: '0.01',
                }}
              />
            )}
          />
        </Grid>

        {/* D A T A   Z A P Ł A T Y */}
        <Grid size={{ xs: 12, md: 4 }} >
          <Controller
            name="paidAt"
            control={control}
            rules={{ required: 'Data zapłaty jest wymagana dla faktury zaliczkowej' }}
            render={({ field }) => (
              <TextField
                type="date"
                label="Data zapłaty *"
                value={field.value || ''}
                onChange={field.onChange}
                onBlur={field.onBlur}
                fullWidth
                error={!!errors.paidAt}
                helperText={
                  errors.paidAt?.message ||
                  'Data wpłynięcia środków na konto (DD.MM.RRRR lub YYYY-MM-DD)'
                }
                InputLabelProps={{
                  shrink: true,
                }}
              />
            )}
          />
        </Grid>

        {/* K O M E N T A R Z   D O   P Ł A T N O Ś C I */}
        <Grid size={{ xs: 12 }} >
          <Controller
            name="paymentComment"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Komentarz do płatności (opcjonalnie)"
                value={field.value || ''}
                fullWidth
                multiline
                rows={2}
                helperText="Domyślnie backend uzupełni automatycznie dla zaliczki. Możesz nadpisać własnym tekstem."
              />
            )}
          />
        </Grid>
      </Grid>
    </AccordionCard>
  );
};

export default InvoicePrepaymentPaymentSection;
